export const CONTROL_JSON = 'control.json';

export const LIVESTREAM_CONTROL_CDN_US = 'https://genesis-cdn.ff.com';
export const LIVESTREAM_CONTROL_CDN_CN = 'https://genesis-cdn.faradayfuturecn.com';

export const LIVESTREAM_CONTROL_PATH_OPTIONS = [
  { value: 'ff-homepage', label: 'US homepage (ff-homepage)' },
  { value: 'ff-homepage-cn', label: 'CN homepage (ff-homepage-cn)' },
];

const ALLOWED_PATHS = new Set(LIVESTREAM_CONTROL_PATH_OPTIONS.map((o) => o.value));

const CONTROL_PATH_IN_URL_RE = /\/assets\/livestream-widgets\/(ff-homepage(?:-cn)?)\/control\.json/;

export function normalizeLivestreamControlPath(path) {
  const trimmed = (path || '').trim();
  return ALLOWED_PATHS.has(trimmed) ? trimmed : '';
}

export function extractLivestreamControlPathFromUrl(url) {
  const match = String(url || '').match(CONTROL_PATH_IN_URL_RE);
  return match ? normalizeLivestreamControlPath(match[1]) : '';
}

export function getLivestreamControlUrl(path) {
  const normalized = normalizeLivestreamControlPath(path);
  if (!normalized) return '';
  return `${LIVESTREAM_CONTROL_CDN_US}/assets/livestream-widgets/${normalized}/${CONTROL_JSON}`;
}

export function canonicalizeLivestreamControlUrl(control) {
  const base = control && typeof control === 'object' ? control : {};
  let path = normalizeLivestreamControlPath(base.path);
  if (!path) {
    path = extractLivestreamControlPathFromUrl(base.controlUrl);
  }
  if (path) {
    return getLivestreamControlUrl(path);
  }
  return String(base.controlUrl || '').trim();
}

export function withLivestreamControlDefaults(control) {
  const base = control && typeof control === 'object' ? control : {};
  const path = normalizeLivestreamControlPath(base.path)
    || extractLivestreamControlPathFromUrl(base.controlUrl);
  return {
    ...base,
    path,
    controlUrl: getLivestreamControlUrl(path),
  };
}

export function rewriteCnLivestreamControlUrlsInHtml(html) {
  return String(html || '').replace(
    new RegExp(
      `${LIVESTREAM_CONTROL_CDN_CN.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/assets/livestream-widgets/(ff-homepage(?:-cn)?)/control\\.json`,
      'g'
    ),
    `${LIVESTREAM_CONTROL_CDN_US}/assets/livestream-widgets/$1/${CONTROL_JSON}`
  );
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** US CDN hosts used for livestream control polling (must stay on US CDN on CN pages). */
export function getLivestreamUsCdnHostPattern() {
  return escapeRegExp(LIVESTREAM_CONTROL_CDN_US);
}

/** Baked ff-livestream JS strings that must not be CN-swapped at publish time. */
export function livestreamWidgetAssetPrefixPattern() {
  return new RegExp(
    `${getLivestreamUsCdnHostPattern()}/assets/livestream-widgets/`,
    'g'
  );
}

export function livestreamControlCdnConstantPattern() {
  return new RegExp(
    `LIVESTREAM_CONTROL_CDN\\s*=\\s*['"]${getLivestreamUsCdnHostPattern()}['"]`,
    'g'
  );
}

/**
 * CN publish runs a global genesis-cdn.ff.com → faradayfuturecn.com swap on the
 * full HTML, including _resolvedData.js. Without this fix, LIVESTREAM_CONTROL_CDN
 * and canonicalizeControlPollUrl's replacement string get corrupted so polling
 * still hits the CN CDN.
 */
export function rewriteCorruptedLivestreamPollingJsInHtml(html) {
  const cnHost = escapeRegExp(LIVESTREAM_CONTROL_CDN_CN);
  const usHost = LIVESTREAM_CONTROL_CDN_US;
  let result = String(html || '');

  result = result.replace(
    new RegExp(`LIVESTREAM_CONTROL_CDN\\s*=\\s*['"]${cnHost}['"]`, 'g'),
    `LIVESTREAM_CONTROL_CDN = '${usHost}'`
  );
  result = result.replace(
    new RegExp(`'${cnHost}/assets/livestream-widgets/'`, 'g'),
    `'${usHost}/assets/livestream-widgets/'`
  );

  return result;
}

function shouldNormalizeLiveControl(control) {
  if (!control || typeof control !== 'object') return false;
  if (normalizeLivestreamControlPath(control.path)) return true;
  if (extractLivestreamControlPathFromUrl(control.controlUrl)) return true;
  return false;
}

function normalizeLiveControlProp(control) {
  if (!shouldNormalizeLiveControl(control)) return control;
  return withLivestreamControlDefaults(control);
}

export function normalizeLivestreamWidgetProps(props) {
  if (!props || typeof props !== 'object') return props;
  let next = { ...props };

  if (shouldNormalizeLiveControl(next.liveControl)) {
    next = {
      ...next,
      liveControl: normalizeLiveControlProp(next.liveControl),
    };
  }

  const widgetPropsBag = next.widgetProps;
  if (
    widgetPropsBag
    && typeof widgetPropsBag === 'object'
    && !Array.isArray(widgetPropsBag)
    && shouldNormalizeLiveControl(widgetPropsBag.liveControl)
  ) {
    next = {
      ...next,
      widgetProps: {
        ...widgetPropsBag,
        liveControl: normalizeLiveControlProp(widgetPropsBag.liveControl),
      },
    };
  }

  return next;
}

function visitComponent(component) {
  if (!component || typeof component !== 'object') return;

  if (component.widgetId === 'CustomWidget' && component.props) {
    const name = component.props.customWidgetName;
    const isLivestreamWidget = name === 'ff-livestream';

    if (component.props.liveControl && (isLivestreamWidget || shouldNormalizeLiveControl(component.props.liveControl))) {
      component.props.liveControl = normalizeLiveControlProp(component.props.liveControl);
    }
    if (component.props.widgetProps?.liveControl
      && (isLivestreamWidget || shouldNormalizeLiveControl(component.props.widgetProps.liveControl))) {
      component.props.widgetProps.liveControl = normalizeLiveControlProp(
        component.props.widgetProps.liveControl
      );
    }
  }

  const nested = [
    ...(component.subcomponents || []),
    ...(component.props?.components || []),
    ...(component.props?.children || []),
  ];
  nested.forEach((child) => {
    if (child && typeof child === 'object') visitComponent(child);
  });
}

export function normalizeLivestreamSlots(slots) {
  if (!Array.isArray(slots)) return;
  slots.forEach((slot) => {
    (slot?.components || []).forEach(visitComponent);
  });
}
